/**
 * Types and helpers for the SAP Datasphere agent chat.
 *
 * The agent is stateless: the full conversation is replayed on every turn. It
 * returns items already in *input* form, so they can be appended to the history
 * and sent back as-is on the next turn.
 */

export type AgentItem =
  | AgentMessageItem
  | AgentFunctionCallItem
  | AgentFunctionCallOutputItem;

export interface AgentMessageItem {
  type: "message";
  role: "user" | "assistant" | "system";
  content: string | AgentContentPart[];
}

export interface AgentContentPart {
  type: string;
  text?: string;
}

export interface AgentFunctionCallItem {
  type: "function_call";
  call_id: string;
  name: string;
  arguments: string;
}

export interface AgentFunctionCallOutputItem {
  type: "function_call_output";
  call_id: string;
  output: unknown;
}

/**
 * Strips the fields the agent adds on output but rejects on replay.
 *
 * Returned items carry `id: "__fake_id__"` and `provider_data`, which are not
 * valid request input, so only the fields needed to rebuild the conversation are kept.
 */
export const toInputItem = (item: any): AgentItem | null => {
  const type = typeof item?.type === "string" ? item.type : "message";

  if (type === "message") {
    if (!item?.role || item?.content === undefined || item?.content === null) return null;
    return { type: "message", role: item.role, content: item.content };
  }
  if (type === "function_call") {
    if (typeof item?.call_id !== "string" || typeof item?.name !== "string") return null;
    return {
      type: "function_call",
      call_id: item.call_id,
      name: item.name,
      arguments: typeof item.arguments === "string" ? item.arguments : "{}",
    };
  }
  if (type === "function_call_output") {
    if (typeof item?.call_id !== "string") return null;
    return { type: "function_call_output", call_id: item.call_id, output: item.output ?? "" };
  }
  // Unknown item types are dropped rather than replayed.
  return null;
};

/** Flattens message content into plain text for rendering. */
export const messageText = (item: AgentMessageItem): string => {
  if (typeof item.content === "string") return item.content;
  return item.content
    .filter((part) => typeof part.text === "string")
    .map((part) => part.text as string)
    .join("");
};

/** Human-readable label for a tool call, e.g. `get_sap_po_schema`. */
export const toolLabel = (item: AgentFunctionCallItem): string => item.name;

/** Pretty-prints a tool result for the expandable detail view. */
export const formatToolOutput = (output: unknown): string => {
  // Outputs arrive as [{ type: "input_text", text: "<json string>" }]
  const raw = Array.isArray(output)
    ? output
        .map((part: any) => (typeof part?.text === "string" ? part.text : JSON.stringify(part)))
        .join("\n")
    : typeof output === "string"
    ? output
    : JSON.stringify(output);

  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};
